import { useCallback, useEffect, useMemo, useState } from "react";
import { useTabActive } from "@/layouts/adminTabs/tabRuntime";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { Calendar, CalendarClock, GripVertical, Loader2, Package, ShoppingCart } from "lucide-react";
import { cn } from "@/lib/utils";
import { fetchAdminOrderSummaries, fetchAdminOrder, transitionOrder } from "@/lib/ordersApi";
import { getApiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import type { OrderDetail, OrderPurchaseListItem, OrderSummary } from "@/lib/types";
import { CLIENT_TYPE_LABELS } from "@/lib/clientTypeLabels";
import { BuyerOrderDialog } from "@/components/admin/BuyerOrderDialog";
import { AdvanceConfirmDialog } from "@/components/admin/AdvanceConfirmDialog";
import { ScheduleProductionDialog } from "@/components/admin/ScheduleProductionDialog";
import { formatDeliveryDate, orderTitle } from "@/lib/orderUtils";

// Background refresh interval — light enough to not hammer the BE.
const POLL_INTERVAL_MS = 15_000;

// Sub-status derived from item decisions — no new BE entity.
type SubStatus = "PARA_COMPRAR" | "EM_NEGOCIACAO" | "COMPRADO";

interface ColumnDef {
  id: SubStatus;
  label: string;
  dotColor: string;
  cardBg: string;
  cardBorder: string;
}

const COLUMNS: ColumnDef[] = [
  { id: "PARA_COMPRAR", label: "Para comprar", dotColor: "bg-muted-foreground/60", cardBg: "bg-surface-elevated", cardBorder: "border-border" },
  { id: "EM_NEGOCIACAO", label: "Em negociação", dotColor: "bg-warning", cardBg: "bg-warning/5", cardBorder: "border-warning/30" },
  { id: "COMPRADO", label: "Comprado / Em produção", dotColor: "bg-success", cardBg: "bg-success/5", cardBorder: "border-success/30" },
];

// `OrderSummary` doesn't carry the purchase list, so we keep a cache of details
// to compute the derived sub-status without hitting the API for each card render.
// `phase` distinguishes pedidos still owned by the buyer ("doing") from those
// that already transitioned ("done") so the last column auto-clears as the
// pedido moves further down the pipeline.
type OrderWithDecisions = OrderSummary & {
  decided: number;
  total: number;
  phase: "doing" | "done";
};

function decisionIsTaken(item: OrderPurchaseListItem): boolean {
  if (item.purchased) return true;
  if (item.useStock) return true;
  if (item.purchasedQuantity != null && item.purchasedQuantity > 0) return true;
  if (item.reason) return true;
  return false;
}

function subStatusFor(order: OrderWithDecisions): SubStatus {
  if (order.phase === "done") return "COMPRADO";
  if (order.total === 0 || order.decided === 0) return "PARA_COMPRAR";
  if (order.decided === order.total) return "COMPRADO";
  return "EM_NEGOCIACAO";
}

export default function DashboardCompras() {
  const [orders, setOrders] = useState<OrderWithDecisions[]>([]);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [advanceTarget, setAdvanceTarget] = useState<OrderWithDecisions | null>(null);
  const [scheduleTarget, setScheduleTarget] = useState<OrderWithDecisions | null>(null);
  // Per-order user override for column position. Lets the buyer drag a card
  // between "Para comprar" and "Em negociação" as a personal todo marker
  // without touching any item. Cleared as soon as the order's decisions change
  // (so the derived state takes over again) or the pedido leaves the board.
  const [overrides, setOverrides] = useState<Record<string, SubStatus>>({});
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // `silent` skips the loading spinner — used by the background poll so the
  // grid doesn't flash empty every 15s while the user is reading it.
  const loadAll = useCallback(async (silent = false) => {
    if (!silent) {
      setLoadState("loading");
      setError(null);
    }
    try {
      const summaries = await fetchAdminOrderSummaries();
      // EM_COMPRA = pedidos still owned by the buyer. EM_PRODUCAO = already
      // handed off; we still show them in the last column so the buyer has a
      // confirmation that the transition went through. They disappear from
      // the board as soon as they move past EM_PRODUCAO.
      const inScope = summaries.filter(
        (o) => o.status === "EM_COMPRA" || o.status === "EM_PRODUCAO",
      );
      const detailed = await Promise.all(
        inScope.map(async (summary) => {
          const phase: "doing" | "done" = summary.status === "EM_COMPRA" ? "doing" : "done";
          if (phase === "done") {
            return { ...summary, decided: 0, total: 0, phase };
          }
          try {
            const detail = await fetchAdminOrder(summary.id);
            const total = detail.purchaseList.length;
            const decided = detail.purchaseList.filter(decisionIsTaken).length;
            return { ...summary, decided, total, phase };
          } catch {
            return { ...summary, decided: 0, total: 0, phase };
          }
        }),
      );
      setOrders(detailed);
      setLoadState("ready");
    } catch (err) {
      if (!silent) {
        setError(getApiErrorMessage(err, "Não foi possível carregar os pedidos"));
        setLoadState("error");
      }
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // Pausa o auto-refresh quando a aba interna está inativa (keep-alive); retoma ao reativar, sem duplicar.
  const tabActive = useTabActive();

  // Lightweight auto-refresh: only when the tab is visible and no dialog is
  // open (avoid stomping on the user reading items mid-decision).
  useEffect(() => {
    if (selectedOrderId || !tabActive) return;
    const tick = () => {
      if (document.visibilityState === "visible") void loadAll(true);
    };
    const id = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [loadAll, selectedOrderId, tabActive]);

  const effectiveSubStatus = (order: OrderWithDecisions): SubStatus =>
    order.phase === "done" ? "COMPRADO" : overrides[order.id] ?? subStatusFor(order);

  const grouped = useMemo(() => {
    const out: Record<SubStatus, OrderWithDecisions[]> = {
      PARA_COMPRAR: [],
      EM_NEGOCIACAO: [],
      COMPRADO: [],
    };
    for (const order of orders) {
      out[effectiveSubStatus(order)].push(order);
    }
    return out;
  }, [orders, overrides]);

  const activeOrder = useMemo(
    () => (activeDragId ? orders.find((o) => o.id === activeDragId) ?? null : null),
    [activeDragId, orders],
  );

  const onOrderChanged = (updated: OrderDetail) => {
    const total = updated.purchaseList.length;
    const decided = updated.purchaseList.filter(decisionIsTaken).length;
    // Decision changed → derivado volta a mandar, override manual é descartado.
    setOverrides((current) => {
      if (!(updated.id in current)) return current;
      const { [updated.id]: _omit, ...rest } = current;
      return rest;
    });
    setOrders((current) => {
      if (updated.status === "EM_COMPRA") {
        const existing = current.find((o) => o.id === updated.id);
        const next = {
          decided,
          total,
          phase: "doing" as const,
          scheduledProductionDate: updated.scheduledProductionDate,
          productionAutoAdvancedPending: updated.productionAutoAdvancedPending,
        };
        if (existing) return current.map((o) => (o.id === updated.id ? { ...o, ...next } : o));
        return current;
      }
      if (updated.status === "EM_PRODUCAO") {
        const existing = current.find((o) => o.id === updated.id);
        const next = { decided: 0, total: 0, phase: "done" as const };
        if (existing) return current.map((o) => (o.id === updated.id ? { ...o, ...next } : o));
        return current;
      }
      // Pedido passou pra EM_EXPEDICAO+ ou foi cancelado: some do board.
      return current.filter((o) => o.id !== updated.id);
    });
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveDragId(null);
    if (!event.over) return;
    const orderId = String(event.active.id);
    const droppedOn = String(event.over.id) as SubStatus;
    const order = orders.find((o) => o.id === orderId);
    if (!order) return;
    if (order.phase === "done") return; // Pedido já avançou — nada a fazer.
    const current = effectiveSubStatus(order);
    if (droppedOn === current) return;

    // Drag pra "Comprado" = avançar pra EM_PRODUCAO. Só pede confirmação se
    // todos os itens já foram decididos; senão pede pra abrir o dialog.
    if (droppedOn === "COMPRADO") {
      if (order.decided < order.total || order.total === 0) {
        toast.info("Decida todos os itens antes de marcar como comprado", {
          action: { label: "Abrir", onClick: () => setSelectedOrderId(orderId) },
        });
        return;
      }
      setAdvanceTarget(order);
      return;
    }

    // Drag entre as 2 primeiras colunas é livre: marca posição manual e segue.
    // Nada vai pro BE — é só um marcador visual do comprador.
    setOverrides((cur) => ({ ...cur, [orderId]: droppedOn }));
  };

  return (
    <div>
      <header className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
        <div className="container py-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium">
            <ShoppingCart className="h-3.5 w-3.5" />
            Dashboard de compras
          </div>
          <h1 className="mt-3 font-display text-3xl font-bold tracking-tight">Compras em andamento</h1>
          <p className="mt-1 text-primary-foreground/80">
            Pedidos aguardando compra. Arraste para mover entre estados ou clique para decidir item a item.
          </p>
        </div>
      </header>

      <div className="container py-5">
        {loadState === "loading" && (
          <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Carregando pedidos...
          </div>
        )}

        {loadState === "error" && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            {error}
          </div>
        )}

        {loadState === "ready" && (
          <>
            <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                {COLUMNS.map((col) => (
                  <KanbanColumn
                    key={col.id}
                    column={col}
                    items={grouped[col.id]}
                    onCardClick={setSelectedOrderId}
                    onSchedule={setScheduleTarget}
                  />
                ))}
              </div>
              <DragOverlay>
                {activeOrder && <OrderPedidoCard order={activeOrder} column={COLUMNS[0]} onClick={() => {}} dragging />}
              </DragOverlay>
            </DndContext>
          </>
        )}
      </div>

      <BuyerOrderDialog
        orderId={selectedOrderId}
        onClose={() => setSelectedOrderId(null)}
        onOrderChanged={onOrderChanged}
      />

      <AdvanceConfirmDialog
        open={advanceTarget != null}
        toStatusLabel="Em produção"
        pedidoLabel={advanceTarget ? orderTitle(advanceTarget) : ""}
        onCancel={() => setAdvanceTarget(null)}
        onConfirm={async (date) => {
          if (!advanceTarget) return;
          try {
            const updated = await transitionOrder(advanceTarget.id, {
              toStatus: "EM_PRODUCAO",
              transitionDate: date,
            });
            onOrderChanged(updated);
            toast.success("Pedido enviado para produção");
            setAdvanceTarget(null);
          } catch (err) {
            toast.error(getApiErrorMessage(err, "Não foi possível avançar o pedido"));
          }
        }}
      />

      <ScheduleProductionDialog
        order={scheduleTarget}
        onClose={() => setScheduleTarget(null)}
        onSaved={onOrderChanged}
      />
    </div>
  );
}

function KanbanColumn({
  column,
  items,
  onCardClick,
  onSchedule,
}: {
  column: ColumnDef;
  items: OrderWithDecisions[];
  onCardClick: (orderId: string) => void;
  onSchedule: (order: OrderWithDecisions) => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "rounded-lg bg-secondary/40 p-3 transition-colors min-h-[300px]",
        isOver && "bg-secondary",
      )}
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={cn("h-2 w-2 rounded-full", column.dotColor)} />
          <span className="text-sm font-semibold">{column.label}</span>
        </div>
        <span className="text-xs text-muted-foreground">{items.length}</span>
      </div>
      <div className="flex flex-col gap-2">
        {items.map((order) => (
          <OrderPedidoCard
            key={order.id}
            order={order}
            column={column}
            onClick={() => onCardClick(order.id)}
            onSchedule={() => onSchedule(order)}
          />
        ))}
        {items.length === 0 && (
          <div className="rounded-md border border-dashed border-border/60 p-4 text-center text-xs text-muted-foreground">
            Nenhum pedido
          </div>
        )}
      </div>
    </div>
  );
}

function OrderPedidoCard({
  order,
  column,
  onClick,
  onSchedule,
  dragging = false,
}: {
  order: OrderWithDecisions;
  column: ColumnDef;
  onClick: () => void;
  onSchedule?: () => void;
  dragging?: boolean;
}) {
  const isDone = order.phase === "done";
  // Drag/click stay disabled for "done" cards — the pedido is already past
  // the buyer's hands. The card only exists as a visual confirmation.
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: order.id, disabled: isDone });
  const preview = order.itemPreview.slice(0, 3).join(", ") + (order.itemPreview.length > 3 ? "..." : "");
  return (
    <div
      ref={setNodeRef}
      onClick={isDone ? undefined : onClick}
      className={cn(
        "group rounded-md border p-3 transition-all",
        column.cardBg,
        column.cardBorder,
        isDone ? "cursor-default opacity-70" : "cursor-pointer",
        (isDragging || dragging) && "opacity-50",
      )}
    >
      {!isDone && onSchedule && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSchedule();
          }}
          className={cn(
            "mb-2 flex w-full items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-semibold transition-colors",
            order.scheduledProductionDate
              ? "border-primary/30 bg-primary/10 text-primary hover:bg-primary/15"
              : "border-dashed border-border text-muted-foreground hover:border-primary/50 hover:text-primary",
          )}
        >
          <CalendarClock className="h-3 w-3 shrink-0" />
          {order.scheduledProductionDate ? (
            <span className="truncate">Produção: {formatDeliveryDate(order.scheduledProductionDate)}</span>
          ) : (
            <span>Agendar produção</span>
          )}
        </button>
      )}

      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-display text-sm font-semibold">
            {order.clientName}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <span>{CLIENT_TYPE_LABELS[order.clientType]}</span>
            {isDone && (
              <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                Em produção
              </span>
            )}
          </div>
        </div>
        {!isDone && (
          <button
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 text-muted-foreground opacity-30 transition-opacity group-hover:opacity-100"
            aria-label="Arrastar pedido"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {order.deliveryWindow ?? order.deliveryDate ?? "—"}
        </span>
        {!isDone && (
          <>
            <span className="text-muted-foreground/40">·</span>
            <span className="inline-flex items-center gap-1">
              <Package className="h-3 w-3" />
              {order.decided}/{order.total} decididos
            </span>
          </>
        )}
      </div>
      {preview && (
        <div className="mt-2 truncate border-t border-border/60 pt-2 text-xs text-muted-foreground">
          {preview}
        </div>
      )}
    </div>
  );
}
